const productionFunctionsOrigin =
  'https://us-central1-frienzzi.cloudfunctions.net';
const productionOrigin = 'https://frienzzi.com';
const productionOrigins = new Set([
  productionOrigin,
  'https://www.frienzzi.com',
  'https://frienzzi.web.app',
]);
const publicLoadError =
  'Unable to load this Frienzzi page. Please refresh and try again.';

function isJsonResponse(response: Response) {
  return response.headers
    .get('content-type')
    ?.toLowerCase()
    .includes('application/json');
}

function directPublicFunctionURL(path: string) {
  const url = new URL(path, productionOrigin);
  const segments = url.pathname.split('/').filter(Boolean);

  if (segments[0] !== 'api' || segments[1] !== 'public') {
    return null;
  }

  const [, , resource, ...resourceSegments] = segments;
  const functionName =
    resource === 'posts'
      ? 'publicPostData'
      : resource === 'profiles'
        ? 'publicProfileData'
        : resource === 'garage'
          ? 'publicGarageVehicleData'
          : null;

  if (!functionName || resourceSegments.length === 0) {
    return null;
  }

  return `${productionFunctionsOrigin}/${functionName}/${resourceSegments.join(
    '/',
  )}${url.search}`;
}

function isProductionOrigin() {
  if (typeof window === 'undefined') {
    return false;
  }

  return productionOrigins.has(window.location.origin);
}

function uniqueCandidates(candidates: (string | null)[]) {
  return candidates.filter(
    (candidate, index): candidate is string =>
      Boolean(candidate) && candidates.indexOf(candidate) === index,
  );
}

function publicApiCandidates(path: string) {
  if (isProductionOrigin()) {
    return [path];
  }

  return uniqueCandidates([
    directPublicFunctionURL(path),
    `${productionOrigin}${path}`,
    path,
  ]);
}

async function parseJson<T>(response: Response) {
  try {
    return (await response.json()) as T;
  } catch {
    throw new Error(publicLoadError);
  }
}

async function fetchJsonCandidate<T>(url: string) {
  let response: Response | null = null;

  try {
    response = await fetch(url, {
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
      },
    });
  } catch {
    response = null;
  }

  if (response && isJsonResponse(response)) {
    return {
      data: await parseJson<T>(response),
      response,
    };
  }

  return null;
}

export async function fetchPublicJson<T>(path: string) {
  for (const candidate of publicApiCandidates(path)) {
    const result = await fetchJsonCandidate<T>(candidate);

    if (result) {
      return result;
    }
  }

  throw new Error(publicLoadError);
}
